import {FC, useEffect, useState} from 'react'
import {UseFormReturn} from 'react-hook-form'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {InputSelectNeuromuscularDiagnosis} from '@/features/patients/components/input-select-neuromuscular-diagnosis/InputSelectNeuromuscularDiagnosis'
import {useTranslation} from '@/translations/i18n'
import {MailerFormValidationSchema} from '@/features/mailer/types'
import {Label} from '@/components/ui/label/Label'
import {NeuroMusocolarDiagnosis} from '@/features/patients/types'
import {SelectProvider, ComboboxProvider} from '@ariakit/react'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectTrigger,
    SelectVirtualList
} from '@/components/ui/select-atoms/SelectAtoms'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {useGetDistricts} from '@/services/useGetDistricts'
import {MailerCollapsibleSection} from '@/features/mailer/components/mailer-collapsible-section/MailerCollapsibleSection'

interface PatientFiltersPanelProps {
    form: UseFormReturn<MailerFormValidationSchema>
}

const SERVICE_TYPE_OPTIONS = [
    {value: 'adults', label: 'mailer:service_type_adults'},
    {value: 'evolutionary_age', label: 'mailer:service_type_evolutionary_age'},
    {value: 'evolutionary_age_adults', label: 'mailer:service_type_evolutionary_age_adults'}
] as const

const GENDER_OPTIONS = [
    {value: 'male', label: 'mailer:gender_male'},
    {value: 'female', label: 'mailer:gender_female'}
] as const

export const PatientFiltersPanel: FC<PatientFiltersPanelProps> = ({form}) => {
    const {t} = useTranslation()
    const {register, setValue, watch} = form
    const serviceType = watch('patientServiceType')
    const gender = watch('patientGender')
    const residenceAslDistrictId = watch('patientResidenceAslDistrictId')
    const [selectedDiagnosis, setSelectedDiagnosis] = useState<NeuroMusocolarDiagnosis | null>(null)

    const districtsQuery = useGetDistricts({responseType: 'small', limit: 100})

    const handleNeuromuscularDiagnosisChange = (value: NeuroMusocolarDiagnosis | null) => {
        setSelectedDiagnosis(value)
        setValue('patientNeuromuscularDiagnosisId', value?.id ?? undefined)
    }

    useEffect(() => {
        if (serviceType === 'evolutionary_age') {
            setValue('patientMaxAge', 18)
        } else if (serviceType === 'adults') {
            setValue('patientMinAge', 18)
        }
    }, [serviceType])

    const activeCount = [
        watch('patientServiceType'),
        watch('patientMinAge'),
        watch('patientMaxAge'),
        watch('patientGender'),
        watch('patientNeuromuscularDiagnosisId'),
        watch('patientResidenceAslDistrictId'),
        watch('patientOutsideRegion') ? true : undefined
    ].filter(Boolean).length

    return (
        <MailerCollapsibleSection
            title={t('mailer:patient_filters_title')}
            activeCount={activeCount}
            activeCountLabel={t('mailer:active_filters_count', {count: activeCount})}
        >
            <Flexbox gap={4} fullWidth style={{flexWrap: 'wrap'}}>
                {/* Service type (first) */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 200px'}}>
                    <ComboboxProvider>
                        <SelectProvider
                            value={serviceType ?? ''}
                            setValue={value => {
                                setValue(
                                    'patientServiceType',
                                    value === ''
                                        ? undefined
                                        : (value as MailerFormValidationSchema['patientServiceType'])
                                )
                            }}
                        >
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>
                                    {t('mailer:service_type')}{' '}
                                    <span className="description">({t('commons:optional')})</span>
                                </Label>
                                <SelectTrigger
                                    placeholder={t('commons:select')}
                                    fullWidth
                                    value={serviceType}
                                    displayValue={() => {
                                        const option = SERVICE_TYPE_OPTIONS.find(o => o.value === serviceType)
                                        return option ? t(option.label) : ''
                                    }}
                                />
                                <SelectComboboxPopover sameWidth>
                                    <SelectComboboxList>
                                        <SelectItem value="">-</SelectItem>
                                        {SERVICE_TYPE_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {t(option.label)}
                                            </SelectItem>
                                        ))}
                                    </SelectComboboxList>
                                </SelectComboboxPopover>
                            </Flexbox>
                        </SelectProvider>
                    </ComboboxProvider>
                </Flexbox>

                {/* Min age */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 150px', maxWidth: '200px'}}>
                    <InputText
                        label={t('mailer:min_age')}
                        type="number"
                        placeholder={t('mailer:min_age_placeholder')}
                        isOptional
                        min={serviceType === 'adults' ? 18 : 0}
                        {...register('patientMinAge', {valueAsNumber: true})}
                    />
                </Flexbox>

                {/* Max age */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 150px', maxWidth: '200px'}}>
                    <InputText
                        label={t('mailer:max_age')}
                        type="number"
                        placeholder={t('mailer:max_age_placeholder')}
                        isOptional
                        min={0}
                        disabled={serviceType === 'evolutionary_age'}
                        {...register('patientMaxAge', {valueAsNumber: true})}
                    />
                </Flexbox>

                {/* Gender */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 200px'}}>
                    <ComboboxProvider>
                        <SelectProvider
                            value={gender ?? ''}
                            setValue={value => {
                                setValue(
                                    'patientGender',
                                    value === '' ? undefined : (value as MailerFormValidationSchema['patientGender'])
                                )
                            }}
                        >
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>
                                    {t('mailer:gender')} <span className="description">({t('commons:optional')})</span>
                                </Label>
                                <SelectTrigger
                                    placeholder={t('mailer:gender_all')}
                                    fullWidth
                                    value={gender}
                                    displayValue={() => {
                                        const option = GENDER_OPTIONS.find(o => o.value === gender)
                                        return option ? t(option.label) : ''
                                    }}
                                />
                                <SelectComboboxPopover sameWidth>
                                    <SelectComboboxList>
                                        <SelectItem value="">{t('mailer:gender_all')}</SelectItem>
                                        {GENDER_OPTIONS.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {t(option.label)}
                                            </SelectItem>
                                        ))}
                                    </SelectComboboxList>
                                </SelectComboboxPopover>
                            </Flexbox>
                        </SelectProvider>
                    </ComboboxProvider>
                </Flexbox>

                {/* Diagnosis */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 250px'}}>
                    <InputSelectNeuromuscularDiagnosis
                        label={t('mailer:disease_type')}
                        value={selectedDiagnosis}
                        onChange={handleNeuromuscularDiagnosisChange}
                        placeholder={t('commons:select')}
                        isOptional
                    />
                </Flexbox>

                {/* ASL district */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 250px'}}>
                    <ComboboxProvider setValue={districtsQuery.onSearch} resetValueOnHide>
                        <SelectProvider
                            value={residenceAslDistrictId ?? ''}
                            setValue={value => {
                                setValue('patientResidenceAslDistrictId', value === '' ? undefined : value)
                            }}
                        >
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>
                                    {t('mailer:asl_district')}{' '}
                                    <span className="description">({t('commons:optional')})</span>
                                </Label>
                                <SelectTrigger
                                    placeholder={t('commons:select')}
                                    fullWidth
                                    value={residenceAslDistrictId}
                                    displayValue={() => {
                                        const match = districtsQuery.remappedData.find(
                                            d => d.id === residenceAslDistrictId
                                        )
                                        return match ? `${match.aslName} - ${match.name}` : ''
                                    }}
                                />
                                <SelectComboboxPopover sameWidth>
                                    <SelectComboboxInput placeholder={t('commons:search_placeholder')} />
                                    <SelectComboboxList>
                                        {districtsQuery.isLoading ? (
                                            <>
                                                {Array.from({length: 6}, (_, index) => (
                                                    <Skeleton key={index} height={32} />
                                                ))}
                                            </>
                                        ) : districtsQuery.isError ? (
                                            <InputHelpText error={t('errors:default')} />
                                        ) : districtsQuery.remappedData.length > 0 ? (
                                            <>
                                                <SelectItem value="">-</SelectItem>
                                                <SelectVirtualList
                                                    data={districtsQuery.remappedData}
                                                    totalCount={districtsQuery.remappedData.length}
                                                    overscan={100}
                                                    endReached={districtsQuery.fetchNextPage}
                                                    itemContent={(_, item) => (
                                                        <SelectItem key={item.id} value={item.id}>
                                                            {item.aslName} - {item.name}
                                                        </SelectItem>
                                                    )}
                                                />
                                            </>
                                        ) : (
                                            <InputHelpText helpText={t('commons:no_results')} />
                                        )}
                                    </SelectComboboxList>
                                </SelectComboboxPopover>
                            </Flexbox>
                        </SelectProvider>
                    </ComboboxProvider>
                </Flexbox>

                {/* Outside region */}
                <Flexbox direction="column" gap={1.5} style={{flex: '1 1 200px'}}>
                    <Label aria-hidden style={{visibility: 'hidden'}}>
                        &nbsp;
                    </Label>
                    <Flexbox align="center" style={{height: 40}}>
                        <Checkbox
                            id="patientOutsideRegion"
                            label={t('mailer:outside_region')}
                            {...register('patientOutsideRegion')}
                        />
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        </MailerCollapsibleSection>
    )
}
